import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { Footer } from '@/components/layout/footer';
import { Logo } from '@/components/layout/logo';
import { ConfirmDialog } from '@/components/ui/dialog';
import welcome from '@/assets/welcome.svg';
import { useSession } from '@/features/auth/use-session';

import { PROGRESS_STEPS, progressIndex, type Step } from './steps';

interface AppShellProps {
  /** Current step, when the progress rail should show. */
  step?: Step;
  children: ReactNode;
}

export function AppShell({ step, children }: AppShellProps) {
  const { signOut } = useSession();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const current = step ? progressIndex(step) : -1;
  const showProgress = current >= 0;

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar__header">
          <Logo />
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setConfirmOpen(true);
            }}
          >
            Sign Out
          </button>
        </div>

        {showProgress && (
          <>
            <p className="progress__mobile">
              <strong>{PROGRESS_STEPS[current]?.label}</strong> · Step {current + 1} of{' '}
              {PROGRESS_STEPS.length}
            </p>
            <nav className="progress--desktop" aria-label="Application progress">
              <p className="progress__title">Start building your profile:</p>
              <ol className="progress">
                {PROGRESS_STEPS.map((s, i) => {
                  const done = i < current;
                  return (
                    <li key={s.step}>
                      <button
                        type="button"
                        className={`progress__item${done ? ' progress__item--done' : ''}`}
                        aria-current={i === current ? 'step' : undefined}
                        disabled={!done}
                        onClick={() => void navigate(`/apply/${s.step}`)}
                      >
                        <span className="progress__dot" aria-hidden="true">
                          {done ? '✓' : i + 1}
                        </span>
                        {s.label}
                      </button>
                    </li>
                  );
                })}
              </ol>
            </nav>
          </>
        )}

        <img src={welcome} alt="" className="sidebar__art" />
      </aside>

      <main className="content">
        <div className="content__inner">{children}</div>
      </main>

      <Footer className="shell__footer" />

      <ConfirmDialog
        open={confirmOpen}
        title="Sign Out"
        body="Are you sure that you would like to sign out?"
        onCancel={() => {
          setConfirmOpen(false);
        }}
        onConfirm={() => {
          setConfirmOpen(false);
          signOut('user');
        }}
      />
    </div>
  );
}
